import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { useApp } from '@/store/appContext';
import KompaLogo from '@/modules/shared/KompaLogo';
import AvatarPicker from './AvatarPicker';
import useSignupValidation, { UPPER_RE, DIGIT_RE, SPECIAL_RE } from './useSignupValidation';

const CEDULA_TYPES = ['V', 'E'];
const PHONE_PREFIXES = ['0412', '0414', '0416', '0422', '0424', '0426'];

const INITIAL_FORM = {
  nombre: '', apellido: '',
  cedulaType: 'V', cedulaNum: '',
  telefonoPrefix: '0412', telefonoNum: '',
  email: '', password: '', confirmPassword: '',
};

const inputBase = "flex-1 bg-transparent outline-none text-base font-medium placeholder:text-gray-400";
const rowBase = (hasError) => `flex items-center gap-3 bg-gray-50 rounded-xl px-4 h-14 transition-all border-b-2 ${hasError ? 'border-red-400' : 'border-transparent focus-within:border-[#fbb040]'} kompa-input`;

export default function SignupPage({ onSwitchToLogin }) {
  const { signup, authError, setAuthError } = useApp();
  const { errors, validate, clearError } = useSignupValidation();

  const [form, setForm] = useState(INITIAL_FORM);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form, avatarPreview)) return;
    setSubmitting(true);
    setAuthError(null);
    try {
      await signup({
        nombre: form.nombre,
        apellido: form.apellido,
        cedula: `${form.cedulaType}-${form.cedulaNum}`,
        telefono: `${form.telefonoPrefix}-${form.telefonoNum}`,
        email: form.email,
        password: form.password,
        avatar: avatarPreview || undefined,
      });
    } catch (err) {
      setAuthError(err.message || 'Error inesperado al crear la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white px-6 md:px-8 pt-10 md:pt-12 pb-8 md:pb-10 max-w-lg mx-auto w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex-1 flex flex-col"
      >
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={onSwitchToLogin}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          <KompaLogo size="sm" />
          <div className="w-10" />
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-black tracking-tight" data-testid="signup-title">
            Crea tu cuenta
          </h1>
          <p className="text-gray-500 font-light mt-1">
            Completa tus datos para comenzar
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 flex-1 no-scrollbar overflow-y-auto" data-testid="signup-form">
          {/* Nombre */}
          <div>
            <div className={rowBase(errors.nombre)}>
              <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input type="text" placeholder="Nombre" value={form.nombre} onChange={(e) => updateField('nombre', e.target.value)} className={inputBase} data-testid="signup-nombre-input" />
            </div>
            {errors.nombre && <p className="text-red-500 text-xs mt-1 ml-1">{errors.nombre}</p>}
          </div>

          {/* Apellido */}
          <div>
            <div className={rowBase(errors.apellido)}>
              <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input type="text" placeholder="Apellido" value={form.apellido} onChange={(e) => updateField('apellido', e.target.value)} className={inputBase} data-testid="signup-apellido-input" />
            </div>
            {errors.apellido && <p className="text-red-500 text-xs mt-1 ml-1">{errors.apellido}</p>}
          </div>

          {/* Cédula compuesta */}
          <div>
            <div className={rowBase(errors.cedulaNum)}>
              <div className="relative flex-shrink-0">
                <select
                  value={form.cedulaType}
                  onChange={(e) => updateField('cedulaType', e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-sm font-bold text-[#1f2d5a] cursor-pointer outline-none focus:border-[#fbb040]"
                  data-testid="signup-cedula-type"
                >
                  {CEDULA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Cédula"
                value={form.cedulaNum}
                onChange={(e) => updateField('cedulaNum', e.target.value.replace(/\D/g, ''))}
                className={inputBase}
                data-testid="signup-cedula-input"
              />
            </div>
            {errors.cedulaNum && <p className="text-red-500 text-xs mt-1 ml-1">{errors.cedulaNum}</p>}
          </div>

          {/* Teléfono compuesto */}
          <div>
            <div className={rowBase(errors.telefonoNum)}>
              <div className="relative flex-shrink-0">
                <select
                  value={form.telefonoPrefix}
                  onChange={(e) => updateField('telefonoPrefix', e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-sm font-bold text-[#1f2d5a] cursor-pointer outline-none focus:border-[#fbb040]"
                  data-testid="signup-telefono-prefix"
                >
                  {PHONE_PREFIXES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Teléfono"
                maxLength={7}
                value={form.telefonoNum}
                onChange={(e) => updateField('telefonoNum', e.target.value.replace(/\D/g, '').slice(0, 7))}
                className={inputBase}
                data-testid="signup-telefono-input"
              />
            </div>
            {errors.telefonoNum && <p className="text-red-500 text-xs mt-1 ml-1">{errors.telefonoNum}</p>}
          </div>

          {/* Email */}
          <div>
            <div className={rowBase(errors.email)}>
              <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input type="email" placeholder="Correo electrónico" value={form.email} onChange={(e) => updateField('email', e.target.value)} className={inputBase} data-testid="signup-email-input" />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email}</p>}
          </div>

          {/* Contraseña + strength meter */}
          <div>
            <div className={rowBase(errors.password)}>
              <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input type={showPassword ? 'text' : 'password'} placeholder="Contraseña" value={form.password} onChange={(e) => updateField('password', e.target.value)} className={inputBase} data-testid="signup-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="signup-toggle-password">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password ? (
              <p className="text-red-500 text-xs mt-1 ml-1">{errors.password}</p>
            ) : form.password.length > 0 ? (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 ml-1">
                <span className={`text-[10px] font-medium ${form.password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`}>8+ caracteres</span>
                <span className={`text-[10px] font-medium ${UPPER_RE.test(form.password) ? 'text-green-500' : 'text-gray-400'}`}>May&uacute;scula</span>
                <span className={`text-[10px] font-medium ${DIGIT_RE.test(form.password) ? 'text-green-500' : 'text-gray-400'}`}>N&uacute;mero</span>
                <span className={`text-[10px] font-medium ${SPECIAL_RE.test(form.password) ? 'text-green-500' : 'text-gray-400'}`}>Especial</span>
              </div>
            ) : null}
          </div>

          {/* Confirmar contraseña */}
          <div>
            <div className={rowBase(errors.confirmPassword)}>
              <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input type={showConfirm ? 'text' : 'password'} placeholder="Confirmar contraseña" value={form.confirmPassword} onChange={(e) => updateField('confirmPassword', e.target.value)} className={inputBase} data-testid="signup-confirm-password-input" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="signup-toggle-confirm">
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1 ml-1">{errors.confirmPassword}</p>}
          </div>

          {/* Avatar */}
          <AvatarPicker
            avatarPreview={avatarPreview}
            onChange={setAvatarPreview}
            error={errors.avatar}
            nameInitial={form.nombre || 'K'}
            showPicker={showAvatarPicker}
            onTogglePicker={() => setShowAvatarPicker(v => !v)}
          />

          <div className="pt-4">
            <motion.button
              type="submit"
              whileTap={{ scale: 0.97 }}
              disabled={submitting}
              className={`w-full bg-[#fbb040] text-black font-bold rounded-full h-14 flex items-center justify-center gap-2 hover:bg-[#ffa000] transition-colors text-lg ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              data-testid="signup-submit-button"
            >
              {submitting ? 'Creando cuenta...' : 'Crear cuenta'}
              {!submitting && <ArrowRight className="w-5 h-5" />}
            </motion.button>
            {authError && <p className="text-red-500 text-sm text-center mt-3" data-testid="signup-error">{authError}</p>}
          </div>
        </form>

        <div className="mt-6 mb-16 text-center relative z-50">
          <p className="text-gray-500 font-light">
            ¿Ya tienes cuenta?{' '}
            <button onClick={onSwitchToLogin} className="font-bold text-[#1f2d5a] hover:underline py-2 px-1" data-testid="switch-to-login">
              Inicia sesión
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
